import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import axiosInstance from '../api/axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes inactivity (when "remember me" is OFF)
const REMEMBER_ME_TIMEOUT_MS = 7 * 24 * 60 * 60 * 1000; // 7 days (matches backend refresh-token TTL)

// Configure axios defaults
axios.defaults.baseURL = API;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimer = useRef(null);

  const logout = useCallback(async () => {
    const token = localStorage.getItem('token');
    // Blacklist token on server
    if (token) {
      try {
        await axios.post('/auth/logout', {}, { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) { /* ignore — token might already be expired */ }
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('rememberMe');
    localStorage.setItem('activeTab', 'dashboard');
    delete axios.defaults.headers.common['Authorization'];
    delete axiosInstance.defaults.headers.common['Authorization'];
    setUser(null);
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
  }, []);

  // Session timeout — reset timer on user activity
  const resetInactivityTimer = useCallback(() => {
    if (!user) return;
    localStorage.setItem('lastActivity', Date.now().toString());
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    const timeout = localStorage.getItem('rememberMe') === '1' ? REMEMBER_ME_TIMEOUT_MS : SESSION_TIMEOUT_MS;
    inactivityTimer.current = setTimeout(() => {
      logout();
    }, timeout);
  }, [user, logout]);

  useEffect(() => {
    if (!user) return;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetInactivityTimer, { passive: true }));
    resetInactivityTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetInactivityTimer));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [user, resetInactivityTimer]);

  // Check if session expired on page load
  useEffect(() => {
    const lastActivity = parseInt(localStorage.getItem('lastActivity') || '0');
    const timeout = localStorage.getItem('rememberMe') === '1' ? REMEMBER_ME_TIMEOUT_MS : SESSION_TIMEOUT_MS;
    if (lastActivity && (Date.now() - lastActivity > timeout)) {
      logout();
      return;
    }
  }, [logout]);

  // Multi-Tab Logout-Sync: wird der Token in einem anderen Tab entfernt
  // (Logout/Session-Expiry), meldet auch dieser Tab den User ab. Verhindert,
  // dass Tab B mit ungültigem Token weiterarbeitet und 401-Fehler produziert.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'token' && e.newValue === null) {
        // Token wurde in anderem Tab gelöscht → hier auch raus
        setUser(null);
        delete axios.defaults.headers.common['Authorization'];
    delete axiosInstance.defaults.headers.common['Authorization'];
        if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const fetchUser = async () => {
    try {
      // axiosInstance enthält den Refresh-Interceptor: bei abgelaufenem Access-
      // Token wird der Refresh-Token (7 Tage TTL) automatisch genutzt, um einen
      // neuen Access-Token zu holen. So bleibt "Angemeldet bleiben (7 Tage)"
      // tatsächlich 7 Tage gültig — auch nach Wieder-Öffnen der App auf dem Handy.
      const response = await axiosInstance.get('/auth/me');
      setUser(response.data);
      // Activity-Touch beim App-Open: stellt sicher, dass der Inactivity-Timer
      // nicht sofort triggert, wenn der User die App nach Tagen wieder öffnet.
      localStorage.setItem('lastActivity', Date.now().toString());
    } catch (error) {
      // axiosInstance.refresh hat bereits ausgelöst (und ggf. zur Login-Seite
      // navigiert wenn auch Refresh fehlschlug). Hier kein zusätzliches logout()
      // mehr — das würde den Refresh-Erfolg überschreiben.
      console.warn('fetchUser failed:', error?.response?.status || error?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password, rememberMe = true) => {
    try {
      const response = await axios.post('/auth/login', { email, password });
      // 2FA challenge: server tells client to complete TOTP verification
      if (response.data?.requires_2fa) {
        // Persist rememberMe so 2FA verify step can read it
        localStorage.setItem('rememberMe', rememberMe ? '1' : '0');
        return { success: false, requires_2fa: true, challenge_token: response.data.challenge_token };
      }
      const { access_token, refresh_token, user } = response.data;

      localStorage.setItem('token', access_token);
      if (refresh_token) localStorage.setItem('refreshToken', refresh_token);
      localStorage.setItem('rememberMe', rememberMe ? '1' : '0');
      localStorage.removeItem('activeTab');
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(user);

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed',
        i18nKey: translateError(error.response?.data?.detail || 'Login failed')
      };
    }
  };

  const verify2fa = async (challenge_token, code) => {
    try {
      const response = await axios.post('/auth/2fa/verify', { challenge_token, code });
      const { access_token, refresh_token, user } = response.data;
      localStorage.setItem('token', access_token);
      if (refresh_token) localStorage.setItem('refreshToken', refresh_token);
      // rememberMe was already set in login() step before 2FA
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(user);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Ungültiger Code' };
    }
  };

  const translateError = (errorMsg) => {
    const errorMap = {
      'Email already registered': 'errors.emailAlreadyRegistered',
      'Registration failed': 'errors.registrationFailed',
      'Invalid or expired verification token': 'auth.verificationFailed',
      'Invalid or expired reset token': 'errors.invalidResetToken',
      'Reset token has expired': 'errors.resetTokenExpired',
      'Aktuelles Passwort ist falsch': 'errors.currentPasswordWrong',
      'EMAIL_NOT_VERIFIED': 'EMAIL_NOT_VERIFIED',
    };
    // Check for password validation errors
    if (errorMsg && (errorMsg.includes('Password must') || errorMsg.includes('Passwort muss') || errorMsg.includes('mindestens'))) {
      return 'errors.passwordRequirements';
    }
    return errorMap[errorMsg] || null;
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/auth/register', userData);
      // Flag for first-login onboarding wizard
      localStorage.setItem('show_onboarding', 'true');
      return { success: true, needsVerification: true, message: response.data.message };
    } catch (error) {
      console.error('Registration error:', error);
      const rawError = error.response?.data?.detail || 'Registration failed';
      const i18nKey = translateError(rawError);
      return { 
        success: false, 
        error: rawError,
        i18nKey: i18nKey
      };
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      updateProfile: async (changes) => {
        const response = await axiosInstance.put('/auth/profile', changes);
        setUser(response.data);
        return response.data;
      },
      login,
      verify2fa,
      register,
      logout,
      loading,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};